import { useMemo, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';

import { ApiError } from '@mds/shared/lib/api';
import { cn } from '@mds/shared/lib/utils';
import {
  COMMUNICATION_CAMPAIGN_KINDS,
  COMMUNICATION_CAMPAIGN_STATUSES,
  type AdminCommunicationCampaign,
  type CommunicationCampaignKind,
  type CommunicationCampaignStatus,
} from '@mds/shared/types/communication';
import { CUSTOMER_CATEGORY_KEYS, type CustomerCategoryKey } from '@mds/shared/types/customer';
import type { AdminEvent } from '@mds/shared/types/event';
import type { NotificationDeliveryStatus } from '@mds/shared/types/notification-history';
import useDebounced from '@mds/shared/hooks/use-debounced';
import Select from '@mds/shared/ui/select';

import { useSession } from '@/auth/use-session';
import * as comms from '@/communications/communications.api';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import ChipPicker from '@/components/ui/chip-picker';
import EmptyState from '@/components/ui/empty-state';
import Field, { controlCls, inputCls, selectCls } from '@/components/ui/field';
import Modal from '@/components/ui/modal';
import Pager from '@/components/ui/pager';
import { Table, Row, Cell } from '@/components/ui/table';
import { SegmentedToggle, Toolbar } from '@/components/ui/toolbar';
import { TAG_POOL } from '@/data/customers';
import {
  KIND_LABELS,
  audienceChangedTo,
  campaignStatus,
  categoryLabel,
  composeSchema,
  errorCode,
  isEmptyAudience,
  normaliseAudience,
} from '@/domain/communications';
import { eventDateLabel } from '@/domain/events';
import * as events from '@/events/events.api';
import { keyOf, stamp } from '@mds/shared/lib/dates';
import { useValidation } from '@/lib/validation';

const ALL = 'all';
const PAGE_SIZE = 20;

interface Compose {
  kind: CommunicationCampaignKind;
  subject: string;
  preheader: string;
  body: string;
}

const blankCompose: Compose = { kind: 'announcement', subject: '', preheader: '', body: '' };

type Notice = { tone: 'ok' | 'warn' | 'bad'; text: string };
const TONES: Record<Notice['tone'], string> = {
  ok: 'border-brand-edge/35 bg-brand-wash text-brand-strong',
  warn: 'border-warn-edge bg-warn-wash text-warn-text',
  bad: 'border-danger-edge bg-danger-wash text-danger-text',
};

const DELIVERY_TONE: Record<NotificationDeliveryStatus, string> = {
  delivered: 'border-brand-edge/35 bg-brand-wash text-brand-strong',
  failed: 'border-danger-edge bg-danger-wash text-danger-text',
  pending: 'border-warn-edge bg-warn-wash text-warn-text',
  processing: 'border-warn-edge bg-warn-wash text-warn-text',
  retry_scheduled: 'border-warn-edge bg-warn-wash text-warn-text',
};
const deliveryLabel = (s: NotificationDeliveryStatus) =>
  s === 'retry_scheduled' ? 'retrying' : s === 'delivered' ? 'accepted' : s;

/**
 * One-off announcements and invitations to customers, picked from the CRM.
 *
 * Only customers who agreed to email are ever counted. The count on screen
 * comes from the server, and a send has to name it: if consent or the CRM
 * moved in between, nothing is queued and the new count is shown for another
 * confirmation. There is no draft, no schedule and no resend.
 */
export default function CommunicationsPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const maySend = can('communications.send');

  const [compose, setCompose] = useState<Compose>(blankCompose);
  const [categories, setCategories] = useState<CustomerCategoryKey[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [picked, setPicked] = useState<AdminEvent[]>([]);
  const draft = useMemo(() => ({ ...compose, tags }), [compose, tags]);
  const validation = useValidation(composeSchema, draft);

  const audience = useMemo(
    () =>
      normaliseAudience({
        categoryKeys: categories,
        tags,
        attendedEventIds: picked.map((e) => e.id),
      }),
    [categories, tags, picked],
  );
  const settledAudience = useDebounced(audience, 400);

  const preview = useQuery({
    queryKey: ['communications', 'preview', settledAudience],
    queryFn: () => comms.previewAudience(settledAudience),
  });
  const previewCurrent =
    JSON.stringify(settledAudience) === JSON.stringify(audience) && !preview.isFetching;
  const count = preview.data?.eligibleCount ?? null;

  const [confirming, setConfirming] = useState(false);
  /** Kept for the life of one confirmation, so a transport retry cannot send twice. */
  const [requestId, setRequestId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [confirmNotice, setConfirmNotice] = useState<string | null>(null);

  /** The audience count is the server's, so it is checked here rather than in the schema. */
  const audienceProblem = (): string | null => {
    if (!previewCurrent || count === null) return 'Wait for the audience count to update.';
    if (count === 0) return 'Nobody who agreed to email matches this audience.';
    if (count > 10_000) return 'That is over 10,000 people. Narrow the audience.';
    return null;
  };

  const openConfirm = () => {
    if (!validation.check()) return;
    const problem = audienceProblem();
    if (problem) return setNotice({ tone: 'bad', text: problem });
    setNotice(null);
    setConfirmNotice(null);
    setRequestId(crypto.randomUUID());
    setConfirming(true);
  };

  const send = async () => {
    if (count === null || requestId === null) return;
    setSending(true);
    setConfirmNotice(null);
    try {
      const campaign = await comms.sendCampaign({
        requestId,
        kind: compose.kind,
        subject: compose.subject.trim(),
        ...(compose.preheader.trim() ? { preheader: compose.preheader.trim() } : {}),
        body: compose.body.trim(),
        audience,
        expectedRecipientCount: count,
      });
      setConfirming(false);
      setRequestId(null);
      setCompose(blankCompose);
      validation.reset();
      setNotice({
        tone: 'ok',
        text: `${campaign.reference} is queued to ${campaign.recipientCount} ${
          campaign.recipientCount === 1 ? 'person' : 'people'
        }. Delivery progress shows in the history below.`,
      });
      await queryClient.invalidateQueries({ queryKey: ['communications', 'campaigns'] });
    } catch (e) {
      const changed = audienceChangedTo(e);
      if (changed !== null) {
        // Not sent. Refresh the count and make them confirm the new number —
        // never send on to an audience nobody looked at.
        await queryClient.invalidateQueries({ queryKey: ['communications', 'preview'] });
        setRequestId(crypto.randomUUID());
        setConfirmNotice(
          `Nothing was sent. The audience changed to ${changed} ${
            changed === 1 ? 'person' : 'people'
          } since the preview (consent or customer details moved). Check the new count and confirm again.`,
        );
      } else if (e instanceof ApiError) {
        // A definitive answer: this id is spent either way.
        setRequestId(crypto.randomUUID());
        setConfirmNotice(
          errorCode(e) === 'COMMUNICATION_AUDIENCE_EMPTY'
            ? 'Nobody who agreed to email matches this audience any more. Nothing was sent.'
            : errorCode(e) === 'COMMUNICATION_AUDIENCE_TOO_LARGE'
              ? 'The audience is over 10,000 people. Narrow it; nothing was sent.'
              : `${e.message} Nothing was sent.`,
        );
      } else {
        // The request may or may not have arrived. The same id makes a retry
        // safe: the server answers with the campaign it already queued.
        setConfirmNotice(
          'The server could not be reached, so it is not certain whether this was sent. Send again. It will not go out twice.',
        );
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {notice && (
        <p
          role={notice.tone === 'ok' ? 'status' : 'alert'}
          className={'m-0 rounded-xl border px-4 py-3 text-base ' + TONES[notice.tone]}
        >
          {notice.text}
        </p>
      )}

      <div className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        {/* --------------------------------------------------------- message */}
        <Card title="Message">
          <div className="flex flex-col gap-4">
            <div>
              <SegmentedToggle
                value={compose.kind}
                options={COMMUNICATION_CAMPAIGN_KINDS}
                onChange={(kind) => setCompose({ ...compose, kind })}
              />
            </div>
            <Field label="Subject" required {...validation.field('subject')}>
              <input
                className={inputCls}
                value={compose.subject}
                maxLength={180}
                disabled={!maySend}
                placeholder="Community evening at MDS"
                onChange={(e) =>
                  setCompose({ ...compose, subject: e.target.value.replace(/[\r\n]/g, ' ') })
                }
              />
            </Field>
            <Field
              label="Preview line"
              hint="Optional. The grey line inboxes show after the subject."
              {...validation.field('preheader')}
            >
              <input
                className={inputCls}
                value={compose.preheader}
                maxLength={255}
                disabled={!maySend}
                placeholder="Join us this Thursday"
                onChange={(e) =>
                  setCompose({ ...compose, preheader: e.target.value.replace(/[\r\n]/g, ' ') })
                }
              />
            </Field>
            <Field
              label="Message"
              hint="Plain text. Blank lines start a new paragraph. MDS's email layout and an unsubscribe link are added for you."
              required
              {...validation.field('body')}
            >
              <textarea
                rows={9}
                className={inputCls}
                value={compose.body}
                maxLength={20000}
                disabled={!maySend}
                placeholder={
                  'You are invited to our next community evening.\n\nDoors open at 18:00.'
                }
                onChange={(e) => setCompose({ ...compose, body: e.target.value })}
              />
            </Field>
            {!maySend && (
              <p className="m-0 text-base text-ink-muted">
                You can preview audiences and read the history, but sending needs the
                communications.send permission.
              </p>
            )}
          </div>
        </Card>

        {/* -------------------------------------------------------- audience */}
        <Card title="Audience">
          <div className="flex flex-col gap-5">
            <fieldset className="m-0 border-0 p-0">
              <legend className="mb-2 font-mono text-sm font-semibold tracking-wider text-ink-secondary uppercase">
                Categories
              </legend>
              <div className="flex flex-wrap gap-2">
                {CUSTOMER_CATEGORY_KEYS.map((key) => {
                  const on = categories.includes(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      aria-pressed={on}
                      onClick={() =>
                        setCategories(
                          on ? categories.filter((k) => k !== key) : [...categories, key],
                        )
                      }
                      className={cn(
                        'min-h-(--target-min) rounded-xl border px-3 text-base font-semibold transition-colors focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)',
                        on
                          ? 'border-cell-selected-bg bg-cell-selected-bg text-cell-selected-fg'
                          : 'border-hairline bg-surface text-ink-muted hover:border-brand-edge',
                      )}
                    >
                      {categoryLabel(key)}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <ChipPicker
              label="Tags"
              pool={TAG_POOL}
              value={tags}
              onChange={(next) => setTags(next.slice(0, 50))}
              placeholder="Exact tag, as on the customer record"
              {...validation.field('tags', { within: true })}
            />

            <EventPicker picked={picked} onChange={setPicked} />

            <p className="m-0 text-base text-ink-muted">
              {isEmptyAudience(audience)
                ? 'No filters: everyone who agreed to email.'
                : 'Someone must match every kind of filter you set, and any one value within it.'}
            </p>

            <AudienceSummary
              loading={preview.isPending || !previewCurrent}
              error={preview.error}
              count={count}
              sample={preview.data?.sample ?? []}
            />

            {maySend && (
              <Button variant="mint" onClick={openConfirm} disabled={!previewCurrent || !count}>
                {count ? `Review and send to ${count}` : 'Review and send'}
              </Button>
            )}
          </div>
        </Card>
      </div>

      <History />

      {/* ---------------------------------------------------------- confirm */}
      <Modal
        open={confirming}
        onClose={() => !sending && setConfirming(false)}
        title={`Send this ${KIND_LABELS[compose.kind].toLowerCase()}?`}
        footer={
          <>
            <Button
              variant="mint"
              disabled={sending || !previewCurrent || !count}
              onClick={() => void send()}
            >
              {sending
                ? 'Sending…'
                : `Send to ${count ?? '…'} ${count === 1 ? 'person' : 'people'}`}
            </Button>
            <Button variant="quiet" disabled={sending} onClick={() => setConfirming(false)}>
              Back
            </Button>
          </>
        }
      >
        {confirmNotice && (
          <p
            role="alert"
            className="m-0 rounded-xl border border-warn-edge bg-warn-wash px-4 py-3 text-base text-warn-text"
          >
            {confirmNotice}
          </p>
        )}
        <div className="flex flex-col gap-1 rounded-xl border border-hairline bg-raised px-4 py-3">
          <span className="text-lg font-semibold">{compose.subject}</span>
          {compose.preheader && (
            <span className="text-base text-ink-muted">{compose.preheader}</span>
          )}
          <p className="m-0 mt-2 line-clamp-6 text-base whitespace-pre-line">{compose.body}</p>
        </div>
        <p className="m-0 text-base">
          This goes out now to{' '}
          <strong className="font-semibold">
            {previewCurrent && count !== null ? count : '…'} {count === 1 ? 'person' : 'people'}
          </strong>
          . It cannot be recalled or edited, and there is no resend.
        </p>
      </Modal>
    </div>
  );
}

/* ------------------------------------------------------------ pickers --- */

/** Upcoming events whose registered guests should hear from us. */
function EventPicker({
  picked,
  onChange,
}: {
  picked: AdminEvent[];
  onChange: (next: AdminEvent[]) => void;
}) {
  const today = keyOf(new Date());
  // No server-side date floor, so take the latest-dated page and keep what has
  // not happened yet — the programme never runs 100 events ahead.
  const upcoming = useQuery({
    queryKey: ['events', 'comms-picker', today],
    queryFn: () => events.listEvents({ limit: 100, sort: 'eventDate', direction: 'desc' }),
    select: (page) => page.data.filter((e) => e.eventDate >= today).reverse(),
  });
  const options = (upcoming.data ?? []).filter((e) => !picked.some((p) => p.id === e.id));

  return (
    <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
      <legend className="mb-2 font-mono text-sm font-semibold tracking-wider text-ink-secondary uppercase">
        Registered in
      </legend>
      {picked.length > 0 && (
        <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
          {picked.map((e) => (
            <li key={e.id}>
              <button
                type="button"
                onClick={() => onChange(picked.filter((p) => p.id !== e.id))}
                aria-label={`Remove ${e.title}`}
                className="inline-flex min-h-(--target-min) items-center gap-2 rounded-xl border border-cell-selected-bg bg-cell-selected-bg px-3 text-base font-semibold text-cell-selected-fg focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)"
              >
                {e.title} · {eventDateLabel(e.eventDate)} <span aria-hidden="true">×</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <Select
        variant="field"
        className={selectCls}
        value=""
        onChange={(id) => {
          const event = options.find((e) => e.id === id);
          if (event) onChange([...picked, event]);
        }}
        options={options.map((e) => ({
          value: e.id,
          label: `${eventDateLabel(e.eventDate)} · ${e.title}`,
        }))}
        placeholder={
          upcoming.isPending
            ? 'Loading events…'
            : options.length === 0
              ? picked.length > 0
                ? 'Every upcoming event is picked'
                : 'No upcoming events'
              : 'Pick an event…'
        }
        disabled={options.length === 0 || picked.length >= 50}
        aria-label="Registered in"
      />
      {upcoming.isError && (
        <p role="alert" className="m-0 text-base text-danger-text">
          {upcoming.error instanceof ApiError
            ? upcoming.error.message
            : 'The events could not be loaded.'}
        </p>
      )}
    </fieldset>
  );
}

function AudienceSummary({
  loading,
  error,
  count,
  sample,
}: {
  loading: boolean;
  error: unknown;
  count: number | null;
  sample: {
    customerId: string;
    reference: string;
    name: string;
    email: string;
    categoryKey: CustomerCategoryKey;
  }[];
}) {
  if (error)
    return (
      <p role="alert" className="m-0 text-base text-danger-text">
        {error instanceof ApiError ? error.message : 'The audience could not be counted.'}
      </p>
    );
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-hairline bg-raised px-4 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-mono text-sm tracking-wider text-ink-muted uppercase">
          Will receive it
        </span>
        <span className={cn('text-3xl font-semibold tabular-nums', loading && 'text-ink-muted')}>
          {count === null ? '–' : count.toLocaleString()}
        </span>
      </div>
      {!loading && sample.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-1 p-0 text-base">
          {sample.map((s) => (
            <li key={s.customerId} className="flex flex-wrap justify-between gap-x-3">
              <span>
                <span className="font-semibold">{s.name}</span>{' '}
                <span className="text-ink-muted">{s.email}</span>
              </span>
              <span className="font-mono text-md text-ink-muted">
                {s.reference} · {categoryLabel(s.categoryKey)}
              </span>
            </li>
          ))}
          {count !== null && count > sample.length && (
            <li className="text-ink-muted">and {count - sample.length} more</li>
          )}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ history --- */

function History() {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  // Trimmed and capped at the API's 100 characters, so a stray space or a
  // pasted paragraph searches rather than failing.
  const settled = useDebounced(q).trim().slice(0, 100);
  const [kind, setKind] = useState<string>(ALL);
  const [status, setStatus] = useState<string>(ALL);
  const [openId, setOpenId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['communications', 'campaigns', { page, settled, kind, status }],
    queryFn: () =>
      comms.listCampaigns({
        page,
        limit: PAGE_SIZE,
        q: settled || undefined,
        kind: kind === ALL ? undefined : (kind as CommunicationCampaignKind),
        status: status === ALL ? undefined : (status as CommunicationCampaignStatus),
      }),
    // Queued and sending campaigns move on their own as the dispatcher works.
    refetchInterval: (query) =>
      query.state.data?.data.some((c) => c.status === 'queued' || c.status === 'sending')
        ? 10_000
        : false,
  });

  const rows = list.data?.data ?? [];
  const reset =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setPage(1);
    };

  return (
    <Card
      title="Sent"
      action={
        <Toolbar>
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder="Search subject or reference"
            aria-label="Search campaigns"
            className={controlCls + ' w-[240px]'}
          />
          <Select
            value={kind}
            onChange={reset(setKind)}
            options={[
              { value: ALL, label: 'Every kind' },
              ...COMMUNICATION_CAMPAIGN_KINDS.map((k) => ({ value: k, label: KIND_LABELS[k] })),
            ]}
            aria-label="Filter by kind"
          />
          <Select
            value={status}
            onChange={reset(setStatus)}
            options={[
              { value: ALL, label: 'Every status' },
              ...COMMUNICATION_CAMPAIGN_STATUSES.map((s) => ({
                value: s,
                label: campaignStatus(s).label,
              })),
            ]}
            aria-label="Filter by status"
          />
        </Toolbar>
      }
    >
      {list.isPending ? (
        <p className="m-0 text-base text-ink-muted">Loading history…</p>
      ) : list.isError ? (
        <EmptyState
          title="Could not load the history"
          hint={
            list.error instanceof ApiError ? list.error.message : 'The server could not be reached.'
          }
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title={settled || kind !== ALL || status !== ALL ? 'Nothing matches' : 'Nothing sent yet'}
        />
      ) : (
        <Table head={['Reference', 'Subject', 'Kind', 'Recipients', 'Status', 'Sent']}>
          {rows.map((c) => {
            const tone = campaignStatus(c.status);
            return (
              <Row key={c.id} onClick={() => setOpenId(c.id)}>
                <Cell className="font-mono text-md">{c.reference}</Cell>
                <Cell className="font-semibold">{c.subject}</Cell>
                <Cell>{KIND_LABELS[c.kind]}</Cell>
                <Cell className="font-mono text-md tabular-nums">
                  {c.deliveredCount}/{c.recipientCount}
                  {c.failedCount > 0 && (
                    <span className="text-danger-text"> · {c.failedCount} failed</span>
                  )}
                </Cell>
                <Cell>
                  <Chip className={'border ' + tone.cls}>{tone.label}</Chip>
                </Cell>
                <Cell className="font-mono text-md">{stamp(c.createdAt)}</Cell>
              </Row>
            );
          })}
        </Table>
      )}
      <Pager pagination={list.data?.pagination} onPage={setPage} />

      <CampaignModal campaignId={openId} onClose={() => setOpenId(null)} />
    </Card>
  );
}

function CampaignModal({
  campaignId,
  onClose,
}: {
  campaignId: string | null;
  onClose: () => void;
}) {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string>(ALL);

  const campaign = useQuery({
    queryKey: ['communications', 'campaigns', 'one', campaignId],
    queryFn: () => comms.getCampaign(campaignId as string),
    enabled: campaignId !== null,
    refetchInterval: (query) =>
      query.state.data && ['queued', 'sending'].includes(query.state.data.status) ? 5_000 : false,
  });
  const recipients = useQuery({
    queryKey: ['communications', 'campaigns', 'recipients', campaignId, { page, status }],
    queryFn: () =>
      comms.listRecipients(campaignId as string, {
        page,
        limit: PAGE_SIZE,
        status: status === ALL ? undefined : (status as NotificationDeliveryStatus),
      }),
    enabled: campaignId !== null,
  });

  const c = campaign.data;
  return (
    <Modal
      open={campaignId !== null}
      onClose={() => {
        setPage(1);
        setStatus(ALL);
        onClose();
      }}
      title={c ? `${c.reference} · ${c.subject}` : 'Campaign'}
    >
      {campaign.isPending ? (
        <p className="m-0 text-base text-ink-muted">Loading…</p>
      ) : campaign.isError || !c ? (
        <p role="alert" className="m-0 text-base text-danger-text">
          {campaign.error instanceof ApiError
            ? campaign.error.message
            : 'That campaign could not be read.'}
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            <Chip className={'border ' + campaignStatus(c.status).cls}>
              {campaignStatus(c.status).label}
            </Chip>
            <Chip>{KIND_LABELS[c.kind]}</Chip>
            <Chip>
              {c.deliveredCount} accepted · {c.pendingCount} pending · {c.failedCount} failed
            </Chip>
            {c.unsubscribeCount > 0 && <Chip>{c.unsubscribeCount} unsubscribed</Chip>}
          </div>
          <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-5 gap-y-2 text-base">
            <dt className="font-mono text-sm text-ink-muted uppercase">Sent</dt>
            <dd className="m-0">
              {stamp(c.createdAt)}
              {c.createdByName ? ` by ${c.createdByName}` : ''}
            </dd>
            <dt className="font-mono text-sm text-ink-muted uppercase">Audience</dt>
            <dd className="m-0">{audienceText(c.audience)}</dd>
          </dl>
          <div className="rounded-xl border border-hairline bg-raised px-4 py-3">
            {c.preheader && <p className="m-0 mb-2 text-base text-ink-muted">{c.preheader}</p>}
            <p className="m-0 text-base whitespace-pre-line">{c.body}</p>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="font-mono text-sm tracking-wider text-ink-muted uppercase">
              Recipients
            </span>
            <Select
              value={status}
              onChange={(v) => {
                setStatus(v);
                setPage(1);
              }}
              options={[
                { value: ALL, label: 'Every state' },
                ...(
                  ['delivered', 'pending', 'processing', 'retry_scheduled', 'failed'] as const
                ).map((s) => ({ value: s, label: deliveryLabel(s) })),
              ]}
              aria-label="Filter recipients"
            />
          </div>
          {recipients.isPending ? (
            <p className="m-0 text-base text-ink-muted">Loading recipients…</p>
          ) : recipients.isError ? (
            <p role="alert" className="m-0 text-base text-danger-text">
              The recipients could not be loaded.
            </p>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {recipients.data.data.map((r) => (
                <li
                  key={r.id}
                  className="flex flex-col gap-1 rounded-xl border border-hairline px-4 py-3 text-base"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span>
                      <span className="font-semibold">{r.recipientName}</span>{' '}
                      <span className="text-ink-muted">{r.recipientEmail}</span>
                    </span>
                    <span className="flex flex-wrap gap-2">
                      {r.unsubscribedAt && <Chip>Unsubscribed</Chip>}
                      <Chip className={'border ' + DELIVERY_TONE[r.deliveryStatus]}>
                        {deliveryLabel(r.deliveryStatus)}
                      </Chip>
                    </span>
                  </div>
                  {r.lastError && <span className="text-md text-danger-text">{r.lastError}</span>}
                </li>
              ))}
            </ul>
          )}
          <Pager pagination={recipients.data?.pagination} onPage={setPage} />
          <p className="m-0 text-base text-ink-muted">
            "Accepted" means the mail provider took it; bounces are not reported back. For
            lower-level troubleshooting see{' '}
            <Link to="/notifications" className="underline">
              Deliveries
            </Link>
            .
          </p>
        </>
      )}
    </Modal>
  );
}

function audienceText(a: AdminCommunicationCampaign['audience']): string {
  const parts = [
    a.categoryKeys?.length ? a.categoryKeys.map(categoryLabel).join(' or ') : null,
    a.tags?.length ? `tagged ${a.tags.join(' or ')}` : null,
    a.attendedEventIds?.length
      ? `registered in ${a.attendedEventIds.length} event${a.attendedEventIds.length === 1 ? '' : 's'}`
      : null,
  ].filter(Boolean);
  return parts.length ? parts.join(', and ') : 'Everyone who agreed to email';
}
